import { useEffect, useState, useContext, memo, forwardRef, useImperativeHandle, useRef } from 'react';

import { SyncOutlined } from '@ant-design/icons';
import { Button, DatePicker, Input, message, Modal, Form } from 'antd';
import { cloneDeep } from 'lodash-es';
import moment from 'moment';
import { styled } from 'styled-components';

import { asyncBeforeEnquiryAdd } from '@/apis/order';
import { getStockpilleHistory, asyncEnquirySave } from '@/apis/so';
import SvgIcon from '@/components/SvgIcon';
import { LayoutContext } from '@/layouts/baseLayout/provider';
import useUserStore from '@/store/user';
import { _globalFindAndStock } from '@/utils/_globalBuriedPoint';
import { buriedPoint } from '@/utils/buriedPoint';
import Utils from '@/utils/Utils';

const CustomInput = styled(Input)`
  &::placeholder {
    font-size: 12px;
    color: #b8bec9;
  }
`;
const CustomDatePicker = styled(DatePicker)`
  .ant-picker-input > input {
    font-size: 12px;
  }

  .ant-picker-input > input::placeholder {
    font-size: 12px;
    color: #b8bec9;
  }
`;

const Bh = 'bh'; // bh 免费备货

const Zl = 'zl'; //  zl 快速找料

const TabListEnums = [
  {
    name: '免费备货',
    code: Bh,
  },
  {
    name: '快速找料',
    code: Zl,
  },
];

const FindAndStock = forwardRef((props, ref) => {
  const { findAndStockContent, openEnquiry, matchCatalog, matchBrand, providerList, totalCount } = props;
  const keywordVal = findAndStockContent;
  const { resourceData } = useContext(LayoutContext);
  const { userInfo } = useUserStore();
  const [activeTab, setActiveTab] = useState(Bh);
  const [stockModel, setStockModel] = useState('');
  const [planList, setPlanList] = useState([
    { date: '', pickNum: '' },
    { date: '', pickNum: '' },
  ]);
  const [modelList, setModelList] = useState(['', '', '']);
  const [hotMaterial, setHotMaterial] = useState([]);
  const [modalVisible, setModalVisible] = useState(false);
  const [form] = Form.useForm();
  const [starProductModel, setStarProductModel] = useState('');
  const [starExpectNumber, setStarExpectNumber] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [isFixed, setIsFixed] = useState(false);
  const [bottomValue, setBottomValue] = useState(20);

  const showStarPrice = openEnquiry && !(matchCatalog || matchBrand);
  const [showXj, setShowXj] = useState(true);
  const [loading, setLoading] = useState(false);

  const zlBtnRef = useRef(null);
  const starPriceRef = useRef(null);
  const bhBtnRef = useRef(null);

  useEffect(() => {
    getStockHistoryFun();
  }, []);

  useEffect(() => {
    setShowXj(providerList?.length + totalCount > 3);
  }, [providerList, totalCount]);

  const hotMaterialRef = useRef(null);
  const [fixedLeft, setFixedLeft] = useState(0);

  const setStarPosition = () => {
    if (!showStarPrice || !showXj) return;
    if (hotMaterialRef.current) {
      const hotMaterialRefDom = hotMaterialRef.current.getBoundingClientRect();
      setFixedLeft(hotMaterialRefDom?.left);
      const documentHeight = document.documentElement.scrollHeight;
      const windowHeight = window.innerHeight;
      const footerDom = document.querySelector('#lcsc-footer');
      const footerDomHeight = footerDom?.offsetHeight;
      const h = Utils.isLgScreen() ? 131 : 91;
      if (window.scrollY === 0 || window.innerHeight - hotMaterialRefDom?.bottom < h + 200) {
        setIsFixed(false);
      } else {
        setIsFixed(true);
        const fromBottomHeight = documentHeight - (window.scrollY + windowHeight);
        // 如果距离底部的动态距离小于底部高度 代表与底部重合了 隐藏快速询价
        if (fromBottomHeight < footerDomHeight) {
          setBottomValue(footerDomHeight - fromBottomHeight + 20);
        } else {
          setBottomValue(20);
        }
      }
    }
  };

  useImperativeHandle(ref, () => ({
    setStarPosition,
  }));

  const getStockHistoryFun = async () => {
    const res = await getStockpilleHistory();
    if (res?.code === 200) {
      setHotMaterial(res?.result?.hotMaterial || []);
    }
  };

  const getNewStockHistoryFun = () => {
    buriedPoint({
      op: _globalFindAndStock['change_batch'],
    });
    const currentPageState = currentPage >= Math.ceil(hotMaterial.length / 10) ? 1 : currentPage + 1;
    setCurrentPage(currentPageState);
  };

  const changeActiveTab = (tab) => {
    setActiveTab(tab);
    if (tab === Bh) {
      buriedPoint({
        op: _globalFindAndStock['free_stock'],
      });
    } else if (tab === Zl) {
      buriedPoint({
        op: _globalFindAndStock['quick_find'],
      });
    }
  };

  const changePlanList = (e, index, key) => {
    const planListState = cloneDeep(planList);
    if (key === 'date') {
      planListState[index][key] = e ? e.format('YYYY-MM-DD') : '';
      if (!checkDates(planListState)) {
        message.error('后续计划的时间不得早于前计划的时间，请重新填写');
        planListState[index][key] = '';
      }
    } else {
      planListState[index][key] = e.target.value;
    }
    setPlanList(planListState);
  };

  const handleBhKeyDown = (e) => {
    if (e.key === 'Enter') {
      stock('', {
        planListState: planList,
        stockModelState: stockModel,
      });
    }
  };

  const checkDates = (dates) => {
    const isInvalidOrder = dates.some((current, index) => {
      if (index < dates.length - 1) {
        const currentDate = current.date ? moment(current.date) : null;
        const nextDate = dates[index + 1].date ? moment(dates[index + 1].date) : null;
        if (!currentDate || !nextDate) return false;
        return currentDate.isAfter(nextDate);
      }
      return false;
    });
    return !isInvalidOrder;
  };

  // 免费备货
  const stock = (dom, { planListState = planList, stockModelState = stockModel }) => {
    if (!keywordVal && !stockModelState) {
      return message.error('请输入型号');
    }
    const pickNums = planListState.map((t) => t.pickNum).filter(Boolean);
    if (pickNums.length) {
      const reg = /^(?!0)(?:[0-9]{1,8}|100000000)$/;
      if (!pickNums.some((t) => reg.test(t))) {
        message.error('请填写正确数量！');
        return;
      }
    }
    let url = `${resourceData?.projectConfigVO?.lcscOrderUrl}/stock/and/find.html?type=stock`;
    let isCarryingParams;
    if (stockModelState) {
      url += `&stockModel=${encodeURI(encodeURI(stockModelState))}`;
      isCarryingParams = true;
    } else {
      url += `&stockModel=${encodeURI(encodeURI(keywordVal))}`;
      isCarryingParams = !!keywordVal;
    }
    if (planListState.some((t) => t.date || t.pickNum)) {
      url += `&planList=${encodeURIComponent(JSON.stringify(planListState.filter((p) => p.date || p.pickNum)))}`;
      isCarryingParams = true;
    }
    if (dom) {
      window.open(Utils.getBtnSpmQueryUrl(url, dom), '_blank');
    }
    buriedPoint({
      pf: 'pc',
      ty: 'click',
      op: _globalFindAndStock['SO_GO_STOCK_PAGE'],
      data: {
        stockModel: stockModelState || keywordVal,
        isCarryingParams,
        t: Date.now(),
      },
    });
  };

  const handleInputChange = (index, value) => {
    const modelListState = cloneDeep(modelList);
    modelListState[index] = value;
    setModelList(modelListState);
  };

  const handleZlKeyDown = (e) => {
    if (e.key === 'Enter') {
      find('', {
        modelListState: modelList,
      });
    }
  };

  // 我要找料
  const find = (dom, { modelListState = modelList }) => {
    if (!keywordVal && !modelListState[0]) {
      return message.error('请输入型号');
    }
    let url = `${resourceData?.projectConfigVO?.lcscOrderUrl}/stock/and/find.html`;
    let isCarryingParams;
    if (modelListState.filter((item) => item).length) {
      url += `?models=${encodeURI(encodeURI(modelListState.filter(Boolean).join('|')))}`;
      isCarryingParams = true;
    } else {
      url += `?models=${encodeURI(encodeURI(keywordVal))}`;
      isCarryingParams = !!keywordVal;
    }
    if (dom) {
      window.open(Utils.getBtnSpmQueryUrl(url, dom), '_blank');
    }
    buriedPoint({
      pf: 'pc',
      ty: 'click',
      op: _globalFindAndStock['SO_GO_FIND_PAGE'],
      data: {
        models: modelListState.filter(Boolean).join() || keywordVal,
        isCarryingParams,
        t: Date.now(),
      },
    });
  };

  const disabledDate = (v) => {
    return !moment(v).isAfter(moment().add(14, 'd')) || !moment(v).isBefore(moment().add(2, 'y'));
  };

  const handleShowModal = (item) => {
    buriedPoint({
      pf: 'pc',
      ty: 'click',
      op: _globalFindAndStock['SO_GO_HOT_MATERIAL'],
      data: {
        hotMaterial: item,
        isLogin: userInfo.customerLogin === 1,
        t: Date.now(), // 当前时间戳,
      },
    });
    if (userInfo?.customerLogin) {
      setModalVisible(true);
      form.setFieldsValue({
        productModel: item?.productModel,
        brandName: item?.brandName,
        expectNumber: '',
      });
    } else {
      Utils.showWindowLogin();
    }
  };

  const handleSubmit = async (valuse) => {
    if (loading) return false;
    setLoading(true);
    const res = await asyncEnquirySave(JSON.stringify([valuse])).finally(() => {
      setLoading(false);
    });
    if (res.code === 200) {
      message.success('找料需求提交成功，我们的工作人员将尽快联系您。');
      setModalVisible(false);
    }
  };

  const handleModalCancel = () => {
    setModalVisible(false);
  };

  const changStarInput = (e, key) => {
    const fnMap = {
      starProductModel: () => {
        setStarProductModel(e.target.value);
      },
      starExpectNumber: () => {
        setStarExpectNumber(e.target.value);
      },
    };
    typeof fnMap[key] === 'function' && fnMap[key]();
  };

  const handleStarPrice = async () => {
    if (!starProductModel || !starExpectNumber) {
      return message.error('请输入型号以及数量');
    }
    if (!starProductModel) {
      return message.error('请输入型号');
    }
    if (!starExpectNumber) {
      return message.error('请输入数量');
    }
    const numPattern = /^(0|[1-9]\d*)$/;
    if (!numPattern.test(starExpectNumber)) {
      return message.error('请输入正确数量');
    }
    buriedPoint({
      op: _globalFindAndStock['find_price'],
    });
    const res = await asyncBeforeEnquiryAdd({
      params: JSON.stringify({
        productModel: starProductModel,
        expectNumber: starExpectNumber,
      }),
      trackZone: 's_s',
    });
    if (res?.needLogin) {
      Utils.showWindowLogin();
      return;
    }
    if (res?.code === 200) {
      const uuid = res?.result;
      window.location.href = Utils.getBtnSpmQueryUrl(
        resourceData?.projectConfigVO?.lcscBomUrl + '/enquiry.html?uuid=' + uuid + '&trackZone=s_s',
        starPriceRef?.current
      );
    }
  };

  const pcbRef = useRef(null);
  const clickAd = () => {
    const href = pcbRef?.current?.href;
    buriedPoint({
      pf: 'pc',
      ty: 'click',
      op: _globalFindAndStock['PCB_SMT'],
      data: {
        hf: href,
      },
    });
  };

  const renderBhHtml = () => {
    return (
      <div className={'px-[15px]'}>
        <p className={'mb-[12px] text-[12px] text-[#444444]'}>给个料号 即用即提</p>
        <ul className="text-[12px] text-[black]">
          <li
            className={
              'mb-[9px] flex h-[28px] items-center rounded-[4px] border-[1px] border-solid border-[#D9D9D9] pl-[7px]'
            }
          >
            <div className="shrink-[0]">
              <span className="text-[#FF0000]">*</span>
              型号
            </div>
            <div className="flex-1">
              <CustomInput
                className="!w-full !text-[12px]"
                placeholder={keywordVal || '商品型号'}
                bordered={false}
                maxLength={100}
                onChange={(e) => {
                  setStockModel(e.target.value);
                }}
                value={stockModel}
                onKeyDown={handleBhKeyDown}
              ></CustomInput>
            </div>
          </li>
          {planList.map((item, index) => {
            return (
              <li
                key={index}
                className={
                  'mb-[9px] flex h-[28px] items-center rounded-[4px] border-[1px] border-solid border-[#D9D9D9] last:mb-0'
                }
              >
                <div className="w-[65px]  pl-[7px]">
                  <div className={`stock-custom-date-${index}`}>
                    <CustomDatePicker
                      className={`!w-full !p-[0] !text-[12px] `}
                      placeholder="提货日期"
                      bordered={false}
                      format={'YY-MM-DD'}
                      suffixIcon={null}
                      allowClear={false}
                      onChange={(e) => changePlanList(e, index, 'date')}
                      style={{
                        fontSize: '12px',
                        color: ' #b8bec9',
                      }}
                      disabledDate={disabledDate}
                      defaultValue={moment().add(14, 'd')}
                      value={item.date ? moment(item.date) : item.date}
                    ></CustomDatePicker>
                  </div>
                </div>
                <div className={'ml-[9px] h-[13px] w-[1px] bg-[#DCDFE6]'}></div>
                <div className="flex-1">
                  <CustomInput
                    className="!w-full !text-[12px]"
                    placeholder="输入数量"
                    bordered={false}
                    maxLength={8}
                    onChange={(e) => changePlanList(e, index, 'pickNum')}
                    onKeyDown={(e) => {
                      handleBhKeyDown(e);
                    }}
                  ></CustomInput>
                </div>
              </li>
            );
          })}
        </ul>
        <div className="flex justify-center pb-[18px] pt-[13px]">
          <button
            className={
              'h-[28px] w-[75px] cursor-pointer rounded-[4px] border border-solid border-[#0093E6] bg-white text-[#0093E6] hover:bg-[#0093E6] hover:text-[#ffffff]'
            }
            ref={bhBtnRef}
            onClick={() => {
              stock(bhBtnRef?.current, {
                planListState: planList,
                stockModelState: stockModel,
              });
            }}
            data-spm={'b'}
          >
            我要备货
          </button>
        </div>
      </div>
    );
  };

  const renderZlHtml = () => {
    return (
      <div className={'px-[15px]'}>
        <p className={'mb-[12px] text-[12px] text-[#444444]'}>快速报价 全球找料</p>
        <ul className="text-[12px] text-[black]">
          {modelList.map((item, index) => (
            <li
              key={index}
              className={`mb-[9px] flex h-[28px] items-center rounded-[4px] border-[1px] border-solid border-[#D9D9D9] pl-[7px] last:mb-0`}
            >
              <div className="shrink-[0]">
                <span className={`${index === 0 ? 'text-[#FF0000]' : 'text-[transparent]'}`}>*</span>
                型号
              </div>
              <div className="flex-1">
                <CustomInput
                  className="!w-full !text-[12px]"
                  value={item}
                  placeholder={index === 0 ? keywordVal || '输入型号' : '输入型号'}
                  bordered={false}
                  maxLength={100}
                  onChange={(e) => {
                    handleInputChange(index, e.target.value);
                  }}
                  onKeyDown={(e) => {
                    handleZlKeyDown(e);
                  }}
                ></CustomInput>
              </div>
            </li>
          ))}
        </ul>
        <div className="flex justify-center pb-[18px] pt-[13px]">
          <button
            className={
              'h-[28px] w-[75px] cursor-pointer rounded-[4px] border border-solid border-[#0093E6] bg-white text-[#0093E6] hover:bg-[#0093E6] hover:text-[#ffffff]'
            }
            ref={zlBtnRef}
            onClick={() => {
              find(zlBtnRef?.current, {
                modelListState: modelList,
              });
            }}
            data-spm={'z'}
          >
            我要找料
          </button>
        </div>
      </div>
    );
  };

  const renderModal = () => {
    return (
      <Modal
        title={'热门找料'}
        keyboard={false}
        maskClosable={false}
        open={modalVisible}
        footer={null}
        onCancel={handleModalCancel}
      >
        <Form
          form={form}
          labelCol={{
            span: 8,
          }}
          wrapperCol={{
            span: 16,
          }}
          autoComplete="off"
          preserve={false}
          onFinish={handleSubmit}
        >
          <Form.Item label="型号" name="productModel" rules={[{ required: true, message: '请输入商品型号' }]}>
            <Input className={'!w-[160px]'} placeholder={'请输入型号'} maxLength={100} />
          </Form.Item>
          <Form.Item label="品牌" name="brandName">
            <Input className={'!w-[160px]'} placeholder={'请输入品牌'} maxLength={32} />
          </Form.Item>
          <Form.Item
            label="数量"
            name="expectNumber"
            rules={[
              {
                required: true,
                message: '请填写正确数量',
                pattern: /^(?!0)(?:[0-9]{1,8}|100000000)$/,
              },
            ]}
          >
            <Input className={'!w-[160px]'} placeholder={'请输入数量'} maxLength={8} />
          </Form.Item>
          <Form.Item label=" " colon={false}>
            <Button type={'primary'} htmlType={'submit'}>
              确定提交
            </Button>
          </Form.Item>
        </Form>
      </Modal>
    );
  };

  const renderStarPrice = () => {
    return (
      <div
        className={'_lg:mt-[131px] mt-[91px] w-[200px]'}
        data-spm="xj"
        style={{
          position: !isFixed ? 'static' : 'fixed',
          bottom: bottomValue + 'px',
          left: fixedLeft + 'px',
        }}
      >
        <div className={'mb-[8px] px-[28px] text-center text-[14px] font-semibold text-[#444444]'}>
          <p>没有找到想要的器件？</p>
          <p>
            试试<span className={'text-[#0093E6]'}>快速询价</span>
          </p>
        </div>
        <ul
          className="h-[144px] rounded-[10px] px-[15px] py-[23px] text-[12px] text-[black]"
          style={{
            background: 'linear-gradient(180deg, #D9EEF9 0%, #FFFFFF 80%)',
          }}
        >
          <li
            className={
              'mb-[9px] flex h-[28px] items-center rounded-[4px] border-[1px] border-solid border-[#D9D9D9] bg-white pl-[7px]'
            }
          >
            <div className="shrink-[0]">
              <span className="text-[#FF0000]">*</span>
              型号
            </div>
            <div className="flex-1">
              <CustomInput
                className="!w-full !text-[12px]"
                placeholder={'请输入型号'}
                bordered={false}
                maxLength={100}
                onChange={(e) => changStarInput(e, 'starProductModel')}
              ></CustomInput>
            </div>
          </li>
          <li
            className={
              'flex h-[28px] items-center rounded-[4px] border-[1px] border-solid border-[#D9D9D9] bg-white  pl-[7px]'
            }
          >
            <div className="shrink-[0]">
              <span className="text-[#FF0000]">*</span>
              数量
            </div>
            <div className="flex-1">
              <CustomInput
                className="!w-full !text-[12px]"
                placeholder={'请输入数量'}
                bordered={false}
                maxLength={8}
                onChange={(e) => changStarInput(e, 'starExpectNumber')}
              ></CustomInput>
            </div>
          </li>
          <li className="flex justify-center pt-[13px]">
            <button
              className={
                'h-[28px] w-[75px] cursor-pointer rounded-[4px] border border-solid border-[#0093E6] bg-white text-[#0093E6] hover:bg-[#0093E6] hover:text-[#ffffff]'
              }
              ref={starPriceRef}
              onClick={handleStarPrice}
              data-spm={'x'}
            >
              我要询价
            </button>
          </li>
        </ul>
      </div>
    );
  };

  return (
    <>
      <div className={'flex flex-col'}>
        <div data-spm="zb">
          <a
            href="https://smt.szlcsc.com"
            data-spm="ad"
            ref={pcbRef}
            aria-label="PCB免费打样 SMT包工包料"
            onClick={clickAd}
          >
            <img
              src="https://static.szlcsc.com/ecp/assets/newWeb/so/pcb-2.png"
              alt="PCB免费打样 SMT包工包料"
              height="164px"
              className={'h-[185px] w-[200px]'}
            />
          </a>
          <div className={'mt-[10px] w-[200px]'}>
            <div className={'mb-[10px]'}>
              <div className={'w-[200px] rounded-[10px]  bg-white'}>
                <div
                  className={'flex rounded-[10px] px-[15px] py-[13px]'}
                  style={{
                    background: 'linear-gradient( 180deg, #D9EEF9 0%, #FFFFFF 100%)',
                  }}
                >
                  {TabListEnums.map((item) => {
                    return (
                      <div
                        key={item.code}
                        className={`w-[60px] ${item.code === Bh ? 'mr-[39px]' : ''} flex flex-col items-center`}
                        onClick={() => changeActiveTab(item.code)}
                      >
                        <div
                          className={`cursor-pointer text-[14px] font-bold leading-[19px] text-[#000000] ${activeTab === item.code ? 'text-[#0093E6]' : ''} hover:text-[#0093E6]`}
                        >
                          {item.name}
                        </div>
                        <div
                          className={`h-[3px] w-[30px] ${activeTab === item.code ? 'bg-[#0093E6]' : ''} rounded-[3px]`}
                        ></div>
                      </div>
                    );
                  })}
                </div>
                {activeTab === Bh && renderBhHtml()}
                {activeTab === Zl && renderZlHtml()}
              </div>
            </div>
            <div className={'mb-[12px] w-[200px] rounded-[10px] bg-white'}>
              <div className={'mb-[7px] flex px-[14px] pt-[14px]'}>
                <div className={'flex-1 text-[14px] font-bold text-[#0093E6]'}>热门找料</div>
                {hotMaterial?.length > 10 && (
                  <div
                    className={'flex-1 cursor-pointer text-right text-[12px] text-[#54657D] hover:text-[#0093E6]'}
                    onClick={getNewStockHistoryFun}
                  >
                    <SyncOutlined />
                    <span className={'pl-[3px]'}>换一批</span>
                  </div>
                )}
              </div>
              <ul className="max-h-[413px] overflow-hidden px-[11px] text-[black]" ref={hotMaterialRef}>
                {hotMaterial.slice((currentPage - 1) * 10, currentPage * 10).map((item, index) => (
                  <li
                    key={index}
                    className="mb-[9px] cursor-pointer"
                    onClick={() => {
                      handleShowModal(item);
                    }}
                  >
                    <div className="mb-[3px] flex">
                      <div
                        className={`w-[16px] pr-[5px] text-right ${item.isKeyPoint ? 'font-bold text-[#F3543D]' : 'text-[#69788a]'}`}
                      >
                        {index + 1 + (currentPage - 1) * 10}
                      </div>
                      <span
                        className={`mr-[5px] flex-1 truncate ${item.isKeyPoint ? 'font-bold' : ''} hover:text-[#0093E6]`}
                      >
                        {item.productModel}
                      </span>
                      {item.isHot && <SvgIcon app="so" iconName={'hot'} className={'h-[19px] w-[16px]'}></SvgIcon>}
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>
          {showXj && showStarPrice && renderStarPrice()}
        </div>
      </div>
      {renderModal()}
    </>
  );
});

FindAndStock.displayName = 'FindAndStock';

export default memo(FindAndStock);
